import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import SignatureCanvas from "../components/SignatureCanvas";
import { AlertCircle, Loader2, X, ArrowLeft } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Generate bullet summary for a category
const generateBulletSummary = (categoryName, items) => {
  const name = (categoryName || "").toLowerCase();
  if (name.includes("deck") || name.includes("framing")) {
    return "Complete deck construction including framing, decking boards, and structural supports.";
  }
  if (name.includes("railing") || name.includes("rail")) {
    return "Professional railing installation with posts, balusters, and top rails for safety and aesthetics.";
  }
  if (name.includes("stair") || name.includes("step")) {
    return "Custom stair construction with stringers, treads, and risers built to code.";
  }
  if (name.includes("electrical") || name.includes("wire")) {
    return "Complete electrical work including outlets, switches, and code-compliant wiring.";
  }
  if (name.includes("plumb")) {
    return "Full plumbing installation with fixtures, pipes, and connections.";
  }
  if (name.includes("paint") || name.includes("finish")) {
    return "Professional finishing including primer, paint, and protective coatings.";
  }
  if (name.includes("demo") || name.includes("prep")) {
    return "Site preparation and demolition of existing structures as needed.";
  }
  if (name.includes("rough") || name.includes("in")) {
    return "Rough-in work including framing, electrical, and plumbing infrastructure.";
  }
  if (name.includes("finish")) {
    return "Final finishes including trim, fixtures, and detail work.";
  }
  if (name.includes("surface")) {
    return "Surface installation and finishing as specified.";
  }
  if (name.includes("labor")) {
    return "Professional labor for all installation and construction work.";
  }
  return `Complete ${categoryName || "work"} as specified in project scope.`;
};

// Generate milestones from categories
const generateMilestones = (categories, duration) => {
  const totalDays = duration?.max || 14;
  const daysPerPhase = Math.ceil(totalDays / 4);
  
  const defaultMilestones = [
    { name: "Demo & Prep", scope: ["Site protection", "Demolition", "Material staging", "Permit posting"] },
    { name: "Rough-In", scope: ["Framing", "Electrical rough", "Plumbing rough", "Inspections"] },
    { name: "Finishes", scope: ["Surface installation", "Fixture mounting", "Trim work", "Paint/finish"] },
    { name: "Final Walkthrough", scope: ["Final inspection", "Punch list", "Client walkthrough", "Handover"] }
  ];
  
  return defaultMilestones.map((m, idx) => ({
    ...m,
    dayRange: `Day ${idx * daysPerPhase + 1}-${Math.min((idx + 1) * daysPerPhase, totalDays)}`
  }));
};

const PublicProposal = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [proposal, setProposal] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [company, setCompany] = useState(null);
  const [displayMode, setDisplayMode] = useState("bulleted");
  
  // Signature state
  const [signerName, setSignerName] = useState("");
  const [signerEmail, setSignerEmail] = useState("");
  const [signatureData, setSignatureData] = useState(null);
  const [signatureType, setSignatureType] = useState("typed");
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  
  // Check for payment return
  const paymentStatus = searchParams.get("payment");

  // Enable scrolling for this page
  useEffect(() => {
    document.body.classList.add('public-proposal-page');
    return () => {
      document.body.classList.remove('public-proposal-page');
    };
  }, []);

  const fetchProposal = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/public/proposal/${token}`);
      setProposal(res.data);
      setEstimate(res.data.estimate);
      setCompany(res.data.company);
      setDisplayMode(res.data.display_default_mode || "bulleted");
      setSignerEmail(res.data.client_email || "");
      setSignerName(res.data.client_name || "");
      
      if (res.data.signature) {
        setSigned(true);
      }
    } catch (error) {
      console.error("Failed to load proposal", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchProposal();
    
    if (paymentStatus === "success") {
      toast.success("Payment successful! Thank you.");
    } else if (paymentStatus === "cancelled") {
      toast.error("Payment was cancelled");
    }
  }, [fetchProposal, paymentStatus]);

  // Calculate totals from estimate sections
  const calculateTotals = useCallback(() => {
    if (!estimate?.sections) return { total: 0, itemCount: 0, categories: [] };
    
    let total = 0;
    let itemCount = 0;
    const categories = [];
    
    estimate.sections.forEach(section => {
      let categoryTotal = 0;
      const items = [];
      
      (section.line_items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = item.unit_cost !== undefined 
          ? parseFloat(item.unit_cost) 
          : ((parseFloat(item.material_unit_cost) || 0) + (parseFloat(item.labor_unit_cost) || 0));
        const markup = parseFloat(item.markup_percent) || 0;
        const basePrice = qty * unitCost;
        const itemTotal = basePrice * (1 + markup / 100);
        
        categoryTotal += itemTotal;
        itemCount++;
        
        items.push({
          name: item.item_name || item.name || "Item",
          description: item.description || "",
          quantity: qty,
          unit: item.unit || "EA",
          unitCost: unitCost,
          total: itemTotal,
          markup: markup
        });
      });
      
      if (items.length > 0) {
        categories.push({
          name: section.section_name || section.name || "Category",
          total: categoryTotal,
          items: items,
          bulletSummary: generateBulletSummary(section.section_name || section.name, items)
        });
        total += categoryTotal;
      }
    });
    
    return { total, itemCount, categories };
  }, [estimate]);

  const { total: grandTotal, categories } = calculateTotals();
  
  // Get milestones
  const estimatedDuration = proposal?.estimated_duration || { min: 10, max: 14 };
  const milestones = generateMilestones(categories, estimatedDuration);
  
  // Payment milestones
  const paymentMilestones = proposal?.payment_milestones || [
    { id: 1, name: "Deposit (Start Date Reserved)", description: "Due upon contract signing", percent: 25 },
    { id: 2, name: "Mid-Project Milestone", description: "Due at rough-in completion", percent: 40 },
    { id: 3, name: "Substantial Completion", description: "Due at finishes completion", percent: 30 },
    { id: 4, name: "Final Closeout", description: "Due upon project completion", percent: 5 }
  ];
  const firstPayment = grandTotal * ((paymentMilestones[0]?.percent || 25) / 100);

  const handleSignatureChange = (dataUrl, typedName) => {
    setSignatureData(dataUrl);
    if (typedName) {
      setSignerName(typedName);
      setSignatureType("typed");
    } else if (dataUrl) {
      setSignatureType("drawn");
    }
  };

  const handleSign = async () => {
    if (!signerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!signerEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (!signatureData) {
      toast.error("Please sign the proposal");
      return;
    }
    
    setSigning(true);
    try {
      await axios.post(`${API_BASE}/public/proposal/${token}/sign`, {
        signer_name: signerName,
        signer_email: signerEmail,
        signature_type: signatureType,
        signature_data: signatureData
      });
      toast.success("Proposal signed successfully!");
      setSigned(true);
      fetchProposal();
    } catch (error) {
      toast.error("Failed to sign proposal");
    } finally {
      setSigning(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Loader2 className="h-12 w-12 text-emerald-400 animate-spin" />
      </div>
    );
  }

  // Not found state
  if (!proposal) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <Toaster position="top-center" />
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-slate-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Proposal Not Found</h1>
          <p className="text-slate-400">This link may have expired or is invalid.</p>
        </div>
      </div>
    );
  }

  // Check if this is a change order proposal
  const isChangeOrder = !!proposal?.change_order_id;
  
  // Benefits/Why Choose Us
  const benefits = proposal?.benefits || [
    { title: "Veteran-Owned", description: "Proudly serving our community with the same dedication and integrity we brought to our service." },
    { title: "Licensed & Insured", description: "Fully licensed and insured for your protection and peace of mind." },
    { title: "30+ Years Experience", description: "Decades of expertise ensuring quality craftsmanship on every project." }
  ];

  return (
    <div 
      className="min-h-screen py-8"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif",
        overflowY: "auto",
        overflowX: "hidden",
        height: "auto",
        minHeight: "100vh"
      }}
    >
      <Toaster position="top-center" />
      
      {/* Close/Back Button - Fixed at top */}
      <div 
        className="fixed top-4 right-4 z-50 flex gap-2"
        style={{ maxWidth: "900px" }}
      >
        <button
          onClick={() => {
            // Try to go back, otherwise close the tab
            if (window.history.length > 1) {
              window.history.back();
            } else {
              window.close();
            }
          }}
          className="flex items-center gap-2 px-4 py-2 bg-slate-800/90 hover:bg-slate-700 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Back to Proposals"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back</span>
        </button>
        <button
          onClick={() => window.close()}
          className="flex items-center justify-center w-10 h-10 bg-slate-800/90 hover:bg-red-600 text-slate-300 hover:text-white rounded-lg border border-slate-600 backdrop-blur-sm transition-all shadow-lg"
          title="Close Tab"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
      
      {/* Document Container */}
      <div 
        className="mx-auto"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: `1px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.2)" : "rgba(16, 185, 129, 0.2)"}`,
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 1: HEADER - Company Info & Proposal Metadata
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 pt-8 pb-6"
          style={{
            background: `linear-gradient(180deg, ${isChangeOrder ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)"} 0%, transparent 100%)`,
            borderBottom: `1px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.2)" : "rgba(16, 185, 129, 0.2)"}`,
            borderRadius: "8px 8px 0 0"
          }}
        >
          {/* Company Name & Info */}
          <div className="mb-6">
            <h1 
              className="text-2xl font-bold tracking-wide"
              style={{ 
                color: isChangeOrder ? "#f59e0b" : "#10b981",
                fontFamily: "'Playfair Display', serif"
              }}
            >
              {company?.company_name || proposal?.company_name || "Alpha Construction Pros"}
            </h1>
            <p className="text-slate-400 text-sm mt-1">
              {company?.address || proposal?.company_address || "123 Builder Lane, Construction City, ST 12345"}
            </p>
            <p className="text-slate-500 text-xs mt-1">
              {company?.email || proposal?.company_email || "info@company.com"} • {company?.phone || proposal?.company_phone || "(555) 123-4567"}
            </p>
          </div>
          
          {/* Proposal Metadata Row */}
          <div className="flex items-center gap-8 flex-wrap">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">PROPOSAL ID</p>
              <p className="text-white font-bold text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {proposal?.estimate_number || estimate?.estimate_id?.toUpperCase() || "EST-00001"}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">DATE</p>
              <p className="text-white font-bold text-sm">
                {proposal?.created_at ? new Date(proposal.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">VALID FOR</p>
              <p className="text-white font-bold text-sm">{proposal?.valid_days || 30} Days</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest">CONTRACT VALUE</p>
              <p 
                className="font-bold text-lg"
                style={{ 
                  color: isChangeOrder ? "#f59e0b" : "#10b981",
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                {fmt(grandTotal)}
              </p>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 2: PROJECT TITLE & INFO CARDS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-8">
          <p 
            className="text-xs uppercase tracking-[4px] mb-2"
            style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
          >
            {isChangeOrder ? `CHANGE ORDER #${proposal?.change_order_number || ''} PROPOSAL` : 'PROJECT PROPOSAL'}
          </p>
          
          {/* Project Title */}
          <h2 
            className="text-3xl font-bold text-white mb-6"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {proposal?.title || estimate?.job_name || "Complete Project Proposal"}
          </h2>
          
          {/* Info Cards Row */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div 
              className="p-4 rounded"
              style={{
                backgroundColor: "rgba(15, 23, 42, 0.8)",
                borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
              }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-wider">ESTIMATED START</p>
              <p className="text-white font-bold text-lg">
                {proposal?.start_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              </p>
            </div>
            <div 
              className="p-4 rounded"
              style={{
                backgroundColor: "rgba(15, 23, 42, 0.8)",
                borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
              }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-wider">ESTIMATED DURATION</p>
              <p className="text-white font-bold text-lg">{estimatedDuration.min}-{estimatedDuration.max} Days</p>
            </div>
            <div 
              className="p-4 rounded"
              style={{
                backgroundColor: "rgba(15, 23, 42, 0.8)",
                borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
              }}
            >
              <p className="text-xs text-slate-500 uppercase tracking-wider">WARRANTY</p>
              <p className="text-white font-bold text-lg">{proposal?.warranty || "1 Year Labor"}</p>
            </div>
          </div>
          
          {/* Prepared For Box */}
          <div 
            className="p-5 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.6)",
              border: `1px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.3)" : "rgba(16, 185, 129, 0.3)"}`,
              borderLeft: `4px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
            }}
          >
            <p 
              className="text-xs uppercase tracking-widest mb-3"
              style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
            >
              PREPARED FOR
            </p>
            <p className="text-white font-bold text-xl mb-1">
              {proposal?.client_name || "Client Name"}
            </p>
            <p className="text-slate-400 text-sm">
              {proposal?.client_email || "client@email.com"}
            </p>
            <p className="text-slate-400 text-sm">
              {proposal?.client_phone || "(555) 000-0000"}
            </p>
            {proposal?.client_address && (
              <p className="text-slate-400 text-sm mt-2">
                {proposal.client_address}
              </p>
            )}
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 3: CLIENT MESSAGE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <p 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
            >
              CLIENT MESSAGE
            </p>
            <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
              {proposal?.client_message || `Dear ${(proposal?.client_name || "Valued Client").split(" ")[0]},

We are delighted to work with you on this project. Our team has carefully reviewed every detail and we're confident that together we can create something truly exceptional. This proposal reflects our commitment to quality craftsmanship, transparent pricing, and delivering results that exceed expectations.

We look forward to bringing your vision to life.`}
            </p>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 4: PROJECT OVERVIEW & TIMELINE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <p 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
            >
              PROJECT OVERVIEW
            </p>
            <p className="text-slate-300 leading-relaxed mb-8">
              {proposal?.project_overview || `This comprehensive project includes all materials, labor, and expertise needed to complete your ${proposal?.title || "project"} to the highest standards. Our team will manage every aspect from initial preparation through final walkthrough, ensuring a seamless experience and exceptional results.`}
            </p>
            
            {/* Milestone Cards */}
            <div className="grid grid-cols-4 gap-4">
              {milestones.map((milestone, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: "rgba(15, 23, 42, 0.8)",
                    border: "1px solid rgba(100, 116, 139, 0.3)"
                  }}
                >
                  <p 
                    className="text-3xl font-black mb-2"
                    style={{ 
                      color: isChangeOrder ? "#f59e0b" : "#10b981",
                      fontFamily: "'Rajdhani', sans-serif"
                    }}
                  >
                    {String(idx + 1).padStart(2, "0")}
                  </p>
                  <p className="text-white font-bold text-sm mb-1">{milestone.name}</p>
                  <p 
                    className="text-lg font-bold mb-3"
                    style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
                  >
                    {milestone.dayRange}
                  </p>
                  <ul className="space-y-1">
                    {(milestone.scope || []).slice(0, 4).map((item, i) => (
                      <li key={i} className="text-slate-400 text-xs flex items-start gap-2">
                        <span style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }} className="mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 5: DETAILED SCOPE & PRICING
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <div className="flex items-center justify-between mb-6">
              <p 
                className="text-xs uppercase tracking-widest"
                style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
              >
                DETAILED SCOPE OF WORK & PRICING
              </p>
              
              {/* Display Mode Toggle */}
              <div className="flex items-center gap-1 bg-slate-900/50 p-1 rounded">
                {[
                  { value: "lump_sum", label: "Lump Sum" },
                  { value: "bulleted", label: "Bulleted" },
                  { value: "item_names", label: "Item Names" },
                  { value: "item_totals", label: "Item Totals" },
                  { value: "full_breakdown", label: "Full Breakdown" }
                ].map(mode => (
                  <button
                    key={mode.value}
                    onClick={() => setDisplayMode(mode.value)}
                    className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                      displayMode === mode.value 
                        ? `${isChangeOrder ? "bg-amber-500/20 text-amber-400 border border-amber-500/50" : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50"}` 
                        : "text-slate-400 hover:text-white"
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Scope Content Based on Display Mode */}
            <div className="space-y-3 mb-6">
              {displayMode === "lump_sum" ? (
                <>
                  {categories.map((category, idx) => (
                    <div key={idx}>
                      <div 
                        className="flex items-center justify-between px-4 py-3 rounded-t"
                        style={{
                          backgroundColor: isChangeOrder ? "rgba(245, 158, 11, 0.15)" : "rgba(16, 185, 129, 0.15)",
                          borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
                        }}
                      >
                        <p className="text-white font-bold">{category.name}</p>
                      </div>
                      <div 
                        className="px-4 py-3 rounded-b border-x border-b"
                        style={{
                          backgroundColor: "rgba(15, 23, 42, 0.3)",
                          borderColor: "rgba(100, 116, 139, 0.2)"
                        }}
                      >
                        <p className="text-slate-400 text-sm">{category.bulletSummary}</p>
                      </div>
                    </div>
                  ))}
                  
                  {/* Total Investment Box for Lump Sum */}
                  <div 
                    className="p-6 rounded-lg text-center mt-6"
                    style={{
                      backgroundColor: isChangeOrder ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)",
                      border: `2px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
                    }}
                  >
                    <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">Complete Project Scope</p>
                    <p 
                      className="text-4xl font-black"
                      style={{ 
                        color: isChangeOrder ? "#f59e0b" : "#10b981",
                        fontFamily: "'JetBrains Mono', monospace"
                      }}
                    >
                      {fmt(grandTotal)}
                    </p>
                  </div>
                </>
              ) : (
                categories.map((category, idx) => (
                  <div key={idx}>
                    <div 
                      className="flex items-center justify-between px-4 py-3 rounded-t"
                      style={{
                        backgroundColor: isChangeOrder ? "rgba(245, 158, 11, 0.15)" : "rgba(16, 185, 129, 0.15)",
                        borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
                      }}
                    >
                      <p className="text-white font-bold">{category.name}</p>
                      {displayMode !== "item_names" && (
                        <p 
                          className="font-bold"
                          style={{ 
                            color: isChangeOrder ? "#f59e0b" : "#10b981",
                            fontFamily: "'JetBrains Mono', monospace"
                          }}
                        >
                          {fmt(category.total)}
                        </p>
                      )}
                    </div>
                    
                    <div 
                      className="px-4 py-3 rounded-b border-x border-b"
                      style={{
                        backgroundColor: "rgba(15, 23, 42, 0.3)",
                        borderColor: "rgba(100, 116, 139, 0.2)"
                      }}
                    >
                      {displayMode === "bulleted" ? (
                        <p className="text-slate-400 text-sm">{category.bulletSummary}</p>
                      ) : displayMode === "item_names" ? (
                        <ul className="space-y-1">
                          {category.items.map((item, i) => (
                            <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                              <span style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }} className="mt-0.5">•</span>
                              <span>{item.name}</span>
                            </li>
                          ))}
                        </ul>
                      ) : displayMode === "item_totals" ? (
                        <div className="space-y-2">
                          {category.items.map((item, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <span className="text-slate-300 text-sm">{item.name}</span>
                              <span 
                                className="text-sm"
                                style={{ 
                                  color: isChangeOrder ? "#f59e0b" : "#10b981",
                                  fontFamily: "'JetBrains Mono', monospace"
                                }}
                              >
                                {fmt(item.total)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {category.items.map((item, i) => (
                            <div key={i} className="flex items-center justify-between">
                              <div>
                                <span className="text-slate-300 text-sm">{item.name}</span>
                                {item.description && (
                                  <p className="text-slate-500 text-xs">{item.description}</p>
                                )}
                              </div>
                              <div className="text-right">
                                <span className="text-slate-500 text-xs mr-3">
                                  {item.quantity} × {fmt(item.unitCost)} / {item.unit}
                                </span>
                                <span 
                                  className="text-sm font-medium"
                                  style={{ 
                                    color: isChangeOrder ? "#f59e0b" : "#10b981",
                                    fontFamily: "'JetBrains Mono', monospace"
                                  }}
                                >
                                  {fmt(item.total)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {/* Investment Box for non-lump-sum modes */}
            {displayMode !== "lump_sum" && (
              <div 
                className="p-6 rounded-lg text-center"
                style={{
                  backgroundColor: isChangeOrder ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)",
                  border: `2px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
                }}
              >
                <p 
                  className="text-xs uppercase tracking-widest mb-2"
                  style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
                >
                  YOUR INVESTMENT
                </p>
                <p 
                  className="text-4xl font-black"
                  style={{ 
                    color: isChangeOrder ? "#f59e0b" : "#10b981",
                    fontFamily: "'JetBrains Mono', monospace"
                  }}
                >
                  {fmt(grandTotal)}
                </p>
              </div>
            )}
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 6: WHY CHOOSE US
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <p 
              className="text-xs uppercase tracking-widest mb-6"
              style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
            >
              WHY CHOOSE US
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className="p-4 rounded-lg"
                  style={{
                    backgroundColor: "rgba(15, 23, 42, 0.8)",
                    border: "1px solid rgba(100, 116, 139, 0.3)"
                  }}
                >
                  <p className="text-white font-bold mb-2">{benefit.title}</p>
                  <p className="text-slate-400 text-sm">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 7: DRAW SCHEDULE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <p 
                  className="text-xs uppercase tracking-widest"
                  style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
                >
                  DRAW SCHEDULE
                </p>
                <p className="text-slate-500 text-xs mt-1">Payment milestones for this project</p>
              </div>
            </div>
            
            <div className="space-y-3 mb-6">
              {paymentMilestones.map((milestone, idx) => (
                <div 
                  key={milestone.id || idx}
                  className="flex items-center justify-between p-4 rounded"
                  style={{
                    backgroundColor: "rgba(30, 41, 59, 0.5)",
                    borderLeft: `3px solid ${isChangeOrder ? "#f59e0b" : "#10b981"}`
                  }}
                >
                  <div>
                    <p className="text-white font-medium">{milestone.name}</p>
                    {milestone.description && (
                      <p className="text-slate-500 text-sm">{milestone.description}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <p 
                      className="font-bold"
                      style={{ 
                        color: isChangeOrder ? "#f59e0b" : "#10b981",
                        fontFamily: "'JetBrains Mono', monospace"
                      }}
                    >
                      {fmt(grandTotal * ((milestone.percent || milestone.percentage || 0) / 100))}
                    </p>
                    <p className="text-slate-500 text-xs">{milestone.percent || milestone.percentage || 0}%</p>
                  </div>
                </div>
              ))}
            </div>
            
            {/* First Payment Due */}
            <div 
              className="p-5 rounded-lg text-center"
              style={{
                backgroundColor: isChangeOrder ? "rgba(245, 158, 11, 0.1)" : "rgba(16, 185, 129, 0.1)",
                border: `1px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.3)" : "rgba(16, 185, 129, 0.3)"}`
              }}
            >
              <p className="text-slate-400 text-sm mb-1">First Payment Due Upon Signing</p>
              <p 
                className="text-2xl font-bold"
                style={{ 
                  color: isChangeOrder ? "#f59e0b" : "#10b981",
                  fontFamily: "'JetBrains Mono', monospace"
                }}
              >
                {fmt(firstPayment)}
              </p>
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 8: TERMS & CONDITIONS
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: "2px solid rgba(100, 116, 139, 0.3)"
            }}
          >
            <p className="text-xs text-slate-500 uppercase tracking-widest mb-4">TERMS & CONDITIONS</p>
            <div className="text-slate-400 text-sm space-y-2 leading-relaxed">
              <p>{proposal?.terms || `This proposal is valid for ${proposal?.valid_days || 30} days from the date above. All work will be performed in accordance with local building codes and industry standards. Payment terms as specified in the draw schedule above. Any changes to the scope of work may result in additional charges. Client is responsible for obtaining necessary permits unless otherwise specified. Contractor maintains appropriate licenses and insurance for all work performed.`}</p>
            </div>
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            SECTION 9: SIGNATURE
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div className="px-10 py-6">
          <div 
            className="p-6 rounded-lg"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.5)",
              borderTop: `2px solid ${isChangeOrder ? "rgba(245, 158, 11, 0.5)" : "rgba(16, 185, 129, 0.5)"}`
            }}
          >
            <p 
              className="text-xs uppercase tracking-widest mb-4"
              style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
            >
              AUTHORIZATION
            </p>
            
            {signed ? (
              <div className="text-center py-8">
                <div 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-lg mb-4"
                  style={{ backgroundColor: "rgba(16, 185, 129, 0.2)" }}
                >
                  <span className="text-emerald-400 text-xl">✓</span>
                  <span className="text-emerald-400 font-bold">Proposal Signed</span>
                </div>
                <p className="text-slate-400 text-sm">
                  Signed by {proposal?.signature?.signer_name} on {proposal?.signature?.signed_at ? new Date(proposal.signature.signed_at).toLocaleDateString() : ""}
                </p>
              </div>
            ) : (
              <>
                <p className="text-slate-400 text-sm mb-6">
                  By signing below, you authorize {company?.company_name || "our company"} to proceed with the work outlined in this proposal.
                </p>
                
                {/* Signer Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider block mb-2">Your Name</label>
                    <input
                      type="text"
                      value={signerName}
                      onChange={(e) => setSignerName(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded px-4 py-3 text-white focus:border-emerald-500 outline-none"
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 uppercase tracking-wider block mb-2">Your Email</label>
                    <input
                      type="email"
                      value={signerEmail}
                      onChange={(e) => setSignerEmail(e.target.value)}
                      className="w-full bg-slate-900/50 border border-slate-700 rounded px-4 py-3 text-white focus:border-emerald-500 outline-none"
                      placeholder="Enter your email"
                    />
                  </div>
                </div>
                
                {/* Signature Canvas */}
                <div className="mb-6">
                  <label className="text-xs text-slate-500 uppercase tracking-wider block mb-2">Your Signature</label>
                  <SignatureCanvas
                    onSignatureChange={handleSignatureChange}
                    initialName={signerName}
                  />
                </div>
                
                {/* Sign Button */}
                <button
                  onClick={handleSign}
                  disabled={signing}
                  className="w-full py-4 rounded-lg font-bold text-lg transition-all"
                  style={{
                    backgroundColor: isChangeOrder ? "#f59e0b" : "#10b981",
                    color: "#0f172a"
                  }}
                >
                  {signing ? "Signing..." : "SIGN & ACCEPT PROPOSAL"}
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* ═══════════════════════════════════════════════════════════════════════════
            FOOTER
            ═══════════════════════════════════════════════════════════════════════════ */}
        <div 
          className="px-10 py-6 text-center"
          style={{ 
            borderTop: "1px solid rgba(100, 116, 139, 0.2)",
            backgroundColor: "rgba(15, 23, 42, 0.5)"
          }}
        >
          <p className="text-slate-500 text-sm">
            {company?.company_name || "Alpha Construction Pros"} • Licensed & Insured
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Thank you for considering us for your project. We look forward to working with you.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicProposal;
